import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, Home, HelpCircle, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import Disclaimer from "@/components/Disclaimer";
import DeviceCard from "@/components/DeviceCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { fetchDevices, fetchGuides } from "@/lib/api";
import { CATEGORIES, GLOSSARY } from "@/data/static";

export default function Category() {
  const { slug } = useParams();
  const cat = CATEGORIES[slug];
  const [all, setAll] = useState([]);
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    fetchDevices().then(setAll).catch(() => {});
    fetchGuides().then(setGuides).catch(() => {});
  }, []);

  const devices = useMemo(() => (cat ? all.filter(cat.pred) : []), [all, cat]);

  if (!cat) return <Navigate to="/fps-finder" replace />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: cat.title,
      itemListElement: devices.map((d, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://gamingkits.online/device/${d.slug}`,
        name: d.name,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: cat.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <>
      <Seo title={cat.metaTitle} description={cat.metaDesc} path={`/category/${slug}`} jsonLd={jsonLd} />

      <div className="border-b border-border bg-graphite">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <nav className="mb-4 flex items-center gap-1.5 text-xs text-slate-500">
            <Link to="/" className="hover:text-white"><Home className="h-3.5 w-3.5" /></Link>
            <ChevronRight className="h-3 w-3" /><span className="text-slate-300">{cat.title}</span>
          </nav>
          <h1 className="font-display text-2xl font-black tracking-tight text-white sm:text-4xl">{cat.h1}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">{cat.intro}</p>
          <span data-testid="category-count" className="mt-3 inline-block rounded-full border border-neon/30 bg-neon/10 px-3 py-1 text-xs font-heading font-semibold text-neon">{devices.length} phones</span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {devices.length === 0 ? (
          <div className="rounded-xl border border-border bg-slateSurface p-10 text-center text-slate-400">No devices in this category yet.</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {devices.map((d) => <DeviceCard key={d.slug} device={d} />)}
          </div>
        )}

        <div className="mt-8"><Disclaimer /></div>

        {/* FAQ */}
        <section className="mt-10">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-white sm:text-2xl"><HelpCircle className="h-5 w-5 text-cyber" /> Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="rounded-xl border border-border bg-slateSurface px-4">
            {cat.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`category-faq-${i}`} className="border-border">
                <AccordionTrigger className="text-left text-sm font-medium text-white hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-300">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* internal links */}
        <section className="mt-10 grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-3 font-heading text-sm font-bold text-white">Related Guides</h3>
            <ul className="space-y-2">
              {guides.slice(0, 4).map((g) => (
                <li key={g.slug}><Link to={`/guides/${g.slug}`} className="text-sm text-slate-300 hover:text-cyber">→ {g.title}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-3 font-heading text-sm font-bold text-white">Glossary Terms</h3>
            <div className="flex flex-wrap gap-2">
              {GLOSSARY.slice(0, 6).map((g) => (
                <Link key={g.term} to="/glossary" className="rounded-md border border-border bg-graphite px-2.5 py-1 text-xs text-slate-300 hover:text-neon">{g.term}</Link>
              ))}
            </div>
          </div>
        </section>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/fps-finder" className="rounded-lg bg-neon px-5 py-3 text-sm font-heading font-bold text-charcoal">Open FPS Finder</Link>
          <Link to="/price-index" className="rounded-lg border border-cyber/40 bg-cyber/10 px-5 py-3 text-sm font-heading font-bold text-cyber">View Price Index</Link>
          <a
            href="https://phoneflip.pk/used-phones"
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid="category-cta-phoneflip"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-500/40 bg-slateSurface px-5 py-3 text-sm font-heading font-bold text-white hover:border-neon/50"
          >
            Buy on PhoneFlip <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </>
  );
}
